import { Command, Option } from 'commander';

import { graphql, graphqlUnauthenticated } from '../graphql/client.ts';
import { Me } from '../graphql/operations/common.ts';
import { DeviceAuthorize, DeviceToken } from '../graphql/operations/hermes.ts';
import {
  clearElevatedCredentials,
  ELEVATION_MAX_SECONDS,
  loadCredentials,
  saveCredentials,
  saveElevatedCredentials,
  type ElevationLevel,
} from '../lib/auth.ts';
import { openBrowser } from '../lib/browser.ts';
import { error, hint, info, success, warn } from '../lib/output.ts';
import { servicesWithoutScope } from '../lib/token-claims.ts';

// TODO: fetch the set of available API scopes dynamically (e.g. from Hermes'
// resource server) once the backend exposes them. Auth0 RBAC grants the
// intersection of requested scopes and the user's assigned permissions by EXACT
// string match — wildcards are not expanded, and unmatched scopes are dropped
// silently. The list below must therefore contain every permission name the
// terraform-auth0 roles can hold (superadmin `*:*`, admin `*:read`, sales
// granular grants), not just the broadest wildcard per service.
const SERVICES = ['atlas', 'core', 'signals', 'billing', 'outreach', 'hermes'];
const ADMIN_API_SCOPES = SERVICES.map((service) => `${service}/*:read`);

export const USER_API_SCOPES = [
  'atlas/organization:read',
  'atlas/reveal:read',
  'outreach/channel_account:read',
  'outreach/channel_account:write',
  'signals/watcher:read',
  'signals/watcher:write',
];

export const ELEVATION_API_SCOPES = {
  sales: ['atlas/*:read', 'billing/offer:*'],
  admin: ADMIN_API_SCOPES,
  superadmin: [...ADMIN_API_SCOPES, ...SERVICES.map((service) => `${service}/*:*`)],
} satisfies Record<ElevationLevel, string[]>;

const DEFAULT_ELEVATION: ElevationLevel = 'admin';
const DEFAULT_SCOPE = `openid profile email offline_access ${USER_API_SCOPES.join(' ')}`;

export function scopeForLogin(scope: string, scopeIsDefault: boolean, level: ElevationLevel | undefined): string {
  if (!level) return scope;

  const requested = scopeIsDefault
    ? `openid profile email ${[...USER_API_SCOPES, ...ELEVATION_API_SCOPES[level]].join(' ')}`
    : scope;

  return requested
    .split(' ')
    .filter((name) => name && name !== 'offline_access')
    .join(' ');
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function setupUserContext(endpoint?: string): Promise<void> {
  const userData = await graphql(Me, undefined, { endpoint });
  const user = userData.me;
  if (!user) return;

  info('');
  info(`  User:     ${user.firstName} ${user.lastName} <${user.email}>`);
  hint('Run `sl whoami` to verify.');
}

/**
 * Says so when the identity provider granted nothing for a service.
 *
 * Without this the loss is invisible: login reports success, and the operator
 * meets it much later as `unknown command 'organizations'`, because the CLI
 * builds its commands from the schema the router filters by the token's scopes.
 */
function reportScopeGrant(requestedScope: string, accessToken: string): void {
  const missing = servicesWithoutScope(requestedScope, accessToken);
  if (missing.length === 0) return;

  warn(`No scope granted for: ${missing.join(', ')}.`);
  info('  Their commands will not appear in `sl --help`, and their fields are');
  info('  filtered out of the schema this CLI builds itself from.');
  hint('Auth0 grants requested scopes by exact string match against your assigned');
  hint('permissions and drops the rest silently — this is a role assignment in the');
  hint('tenant you logged into, not a CLI setting. Ask an administrator, then re-run `sl login`.');
}

export const loginCommand = new Command('login')
  .summary('Log in to your Sortlist account')
  .description(
    'Log in to your Sortlist account\n\n' +
      'Opens a browser to complete authentication, then saves your\n' +
      "credentials locally. If you belong to multiple companies, you'll\n" +
      'be prompted to pick one.\n\n' +
      'Examples:\n' +
      '  sl login                        Log in with the user scopes, no staff scope\n' +
      '  sl login --no-browser           Print the login URL instead of opening it\n' +
      '  sl login --elevate=sales        Sales scopes, for a limited time\n' +
      '  sl login --elevate              Admin (read) scopes, for a limited time\n' +
      '  sl login --elevate=superadmin   Superadmin (write) scopes, for a limited time\n\n' +
      `An elevated token lasts at most ${ELEVATION_MAX_SECONDS / 60} minutes, is never refreshed, and sits\n` +
      'beside your session: once it expires, or after `sl logout --elevated`,\n' +
      'commands fall back to the plain login.',
  )
  .option('--scope <scope>', 'OAuth scope', DEFAULT_SCOPE)
  .option('--no-browser', 'Print the login URL instead of opening a browser')
  .addOption(
    new Option('--elevate [level]', 'Get a short-lived token with staff scopes')
      .choices(Object.keys(ELEVATION_API_SCOPES))
      .preset(DEFAULT_ELEVATION),
  )
  .action(async function (this: Command) {
    const opts = this.optsWithGlobals();
    const level: ElevationLevel | undefined = opts.elevate;
    const scope = scopeForLogin(opts.scope, this.getOptionValueSource('scope') === 'default', level);

    info(level ? `Starting device authorization for ${level} elevation...` : 'Starting device authorization...');

    const authData = await graphqlUnauthenticated(DeviceAuthorize, { input: { scope } }, opts.endpoint);

    const device = authData.deviceAuthorize;
    if (!device) {
      error('Device authorization failed — the server returned an empty response.');
      hint('Check your network connection and try again.');
      hint('If the problem persists, verify the endpoint: sl config show');
      process.exit(2);
    }

    const url = device.verificationUriComplete ?? device.verificationUri;
    info('');
    info(`  Open this URL in your browser:`);
    info(`  ${url}`);
    info('');
    info(`  Enter code: ${device.userCode}`);
    info('');

    if (opts.browser) {
      openBrowser(url);
      info('  (Browser opened automatically)');
      info('');
    }

    info('Waiting for you to complete login in the browser...');

    let interval = device.interval * 1000;
    const deadline = Date.now() + device.expiresIn * 1000;

    while (Date.now() < deadline) {
      await sleep(interval);

      const tokenData = await graphqlUnauthenticated(
        DeviceToken,
        { input: { deviceCode: device.deviceCode } },
        opts.endpoint,
      );

      const token = tokenData.deviceToken;
      if (!token) {
        error('Token exchange failed — the server returned an empty response.');
        hint('Try running `sl login` again.');
        process.exit(2);
      }

      if (token.error === 'authorization_pending') {
        continue;
      }

      if (token.error === 'slow_down') {
        interval += 5000;
        continue;
      }

      if (token.error === 'expired_token') {
        error('The login code expired before you completed authentication.');
        hint('Run `sl login` to generate a new code.');
        process.exit(2);
      }

      if (token.error === 'access_denied') {
        error('Access was denied during authentication.');
        hint('Make sure you approved the request in the browser.');
        hint('If you denied it by mistake, run `sl login` to try again.');
        process.exit(2);
      }

      if (token.error) {
        error(`Authentication failed (${token.error}).`);
        hint('Run `sl login` to try again.');
        process.exit(2);
      }

      if (level && token.accessToken && token.expiresIn) {
        const elevated = saveElevatedCredentials(token.accessToken, token.expiresIn, level);

        success(`Elevated to ${level} until ${new Date(elevated.expiresAt * 1000).toLocaleTimeString()}.`);
        reportScopeGrant(scope, token.accessToken);
        if (!loadCredentials()) hint('You have no regular session: once this expires, run `sl login`.');
        hint('Run `sl logout --elevated` to drop it sooner.');
        return;
      }

      if (token.accessToken && token.refreshToken && token.idToken && token.expiresIn) {
        saveCredentials({
          accessToken: token.accessToken,
          refreshToken: token.refreshToken,
          idToken: token.idToken,
          expiresAt: Math.floor(Date.now() / 1000) + token.expiresIn,
        });
        clearElevatedCredentials();

        success('Logged in successfully.');
        reportScopeGrant(scope, token.accessToken);
        await setupUserContext(opts.endpoint);
        return;
      }
    }

    error('Authentication timed out — no response from the browser.');
    hint('Run `sl login` to start a new session.');
    process.exit(2);
  });
